import { useState } from 'react';

import { ChevronDown, ChevronUp } from 'lucide-react';

import { ChangedFieldItem } from './changed-field-item';
import { ChangedFieldsCellProps, GenericChangedField } from './types';

const MAX_COLLAPSED_ITEMS = 2;

/**
 * Generic expandable "Changed Fields" table cell shared across modules.
 *
 * Renders up to {@link MAX_COLLAPSED_ITEMS} field changes collapsed, with a
 * chevron to expand the rest. All domain-specific behavior is injected via the
 * `strategy` (see {@link ChangedFieldsStrategy}); this shell only owns the
 * preprocess → filter → collapse/expand → empty-state flow.
 */
export const ChangedFieldsCell = ({
  fields,
  strategy,
  isInitial = false,
}: ChangedFieldsCellProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Preprocess first (e.g. inject synthetic rows) so it can affect collapsed view.
  const processedFields = strategy.preprocessFields ? strategy.preprocessFields(fields) : fields;

  // Keep only fields that render something (matches per-domain behavior: a field
  // needs a value, subfields, or to be a group — an empty-old placeholder alone
  // is not enough, since it only renders alongside a present new value).
  const validFields = processedFields.filter((field: GenericChangedField) => {
    const hasValues = field.from || field.to;
    const hasSubfields = field.subfieldChanges && field.subfieldChanges.length > 0;
    return hasValues || hasSubfields || field.isGrouped;
  });

  if (validFields.length === 0) {
    return (
      <div className="flex w-full items-start justify-center gap-2.5 p-4">
        <div className="flex-1 text-sm text-foreground">
          {strategy.getEmptyStateMessage({ isInitial })}
        </div>
      </div>
    );
  }

  const hasMore = validFields.length > MAX_COLLAPSED_ITEMS;

  return (
    <div
      className="relative flex w-full cursor-pointer items-start justify-end gap-2.5 p-4 transition-colors hover:bg-accent/50"
      onClick={() => hasMore && setIsExpanded(!isExpanded)}
    >
      <div className="flex flex-1 flex-col items-start gap-y-2">
        {validFields.map((field, index) => {
          if (!isExpanded && index >= MAX_COLLAPSED_ITEMS) return null;
          return (
            <ChangedFieldItem
              key={`${field.field}-${index}`}
              field={field}
              allFields={validFields}
              strategy={strategy}
              isVisible={true}
            />
          );
        })}
      </div>
      {hasMore && (
        <div className="flex h-4 w-4 shrink-0 items-center justify-center">
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 transition-transform duration-200" />
          ) : (
            <ChevronDown className="h-4 w-4 transition-transform duration-200" />
          )}
        </div>
      )}
    </div>
  );
};
